/**
 * ── Centralized API Client ─────────────────────────────────────────────────────
 *
 *   Single module for ALL HTTP requests in Quick Discount.
 *   Handles BASE_URL, auth headers, query params, and error handling.
 *
 * ── Usage ──────────────────────────────────────────────────────────────────────
 *
 *   import { api } from "../../utils/api";
 *
 *   // Public GET
 *   const res = await api.get("/discounts/");
 *
 *   // Authenticated POST (token from Redux state or localStorage)
 *   const res = await api.post("/discounts/", { title: "Sale" }, token?.access);
 *
 *   // With query params
 *   const res = await api.get("/reviews/", { params: { discount: 5, sort: "newest" } });
 *
 *   // Raw fetch (when you need lower-level control)
 *   import { API_BASE_URL, buildHeaders } from "../../utils/api";
 *   fetch(`${API_BASE_URL}/custom/`, { headers: buildHeaders(token) });
 * ────────────────────────────────────────────────────────────────────────────────
 */

import { BASE_URL } from "./constants";

// ── Re-export for direct access ───────────────────────────────────────────────
export const API_BASE_URL = BASE_URL;

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Resolve auth token from argument, localStorage, or sessionStorage.
 * Tries: 1) explicit token  2) localStorage cached state  3) raw localStorage key
 */
export function resolveToken(token) {
  if (token) return token;

  try {
    // Redux persists token in localStorage under a known key
    const raw = localStorage.getItem("reduxState");
    if (raw) {
      const parsed = JSON.parse(raw);
      const t = parsed?.userState?.token?.access;
      if (t) return t;
    }
  } catch { /* ignore */ }

  // Fallback — stored directly by some older flows
  try {
    return localStorage.getItem("token") || null;
  } catch {
    return null;
  }
}

/**
 * Build standard request headers.
 * @param {string} [authToken] - Optional JWT access token
 * @param {object} [extra] - Additional headers to merge
 * @returns {object}
 */
export function buildHeaders(authToken, extra = {}) {
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
    ...extra,
  };
  const token = resolveToken(authToken);
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

/**
 * Build a fully-qualified URL from a path and optional query params.
 * @param {string}  path   - e.g. "/discounts/" or "discounts/"
 * @param {object}  [params] - Query params as key/value pairs
 * @returns {string}
 */
export function buildUrl(path, params) {
  const clean = path.startsWith("/") ? path : `/${path}`;
  let url = `${BASE_URL}${clean}`;
  if (params) {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null)
    ).toString();
    if (qs) url += `${url.includes("?") ? "&" : "?"}${qs}`;
  }
  return url;
}

// ── API Client ────────────────────────────────────────────────────────────────

export const api = {
  BASE_URL,

  /** GET request */
  async get(path, { params, headers, ...rest } = {}, authToken) {
    const url = buildUrl(path, params);
    return fetch(url, {
      method: "GET",
      headers: buildHeaders(authToken, headers),
      ...rest,
    });
  },

  /** POST request with JSON body */
  async post(path, body, { headers, ...rest } = {}, authToken) {
    return fetch(buildUrl(path), {
      method: "POST",
      headers: buildHeaders(authToken, headers),
      body: body !== undefined ? JSON.stringify(body) : undefined,
      ...rest,
    });
  },

  /** PUT request with JSON body */
  async put(path, body, { headers, ...rest } = {}, authToken) {
    return fetch(buildUrl(path), {
      method: "PUT",
      headers: buildHeaders(authToken, headers),
      body: body !== undefined ? JSON.stringify(body) : undefined,
      ...rest,
    });
  },

  /** PATCH request with JSON body */
  async patch(path, body, { headers, ...rest } = {}, authToken) {
    return fetch(buildUrl(path), {
      method: "PATCH",
      headers: buildHeaders(authToken, headers),
      body: body !== undefined ? JSON.stringify(body) : undefined,
      ...rest,
    });
  },

  /** DELETE request */
  async del(path, { headers, ...rest } = {}, authToken) {
    return fetch(buildUrl(path), {
      method: "DELETE",
      headers: buildHeaders(authToken, headers),
      ...rest,
    });
  },
};

// ── Axios convenience (for components that prefer axios pattern) ──────────────

let axiosInstance = null;

/**
 * Get or create a singleton Axios instance pre-configured with BASE_URL.
 * Import axios separately — this module does not force axios as a dependency.
 *
 * Usage:
 *   import { getAxios } from "../../utils/api";
 *   import axios from "axios";
 *   const http = getAxios(axios, token);
 *   const { data } = await http.get("/discounts/");
 */
export function getAxios(axios, authToken) {
  if (!axiosInstance) {
    axiosInstance = axios.create({
      baseURL: BASE_URL,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    // Response interceptor — normalize errors globally
    axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error("[API] Axios error:", error.config?.url, error.response?.status);
        return Promise.reject(error);
      }
    );
  }

  // Apply auth token per-request (or from singleton if it never changes)
  const token = resolveToken(authToken);
  if (token) {
    axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
  }

  return axiosInstance;
}

/**
 * Reset the Axios singleton (useful on logout).
 */
export function resetAxios() {
  if (axiosInstance) {
    delete axiosInstance.defaults.headers.common.Authorization;
  }
  axiosInstance = null;
}

export default api;
